/**
 * delegation.ts
 * A user's own delegation of their pending tasks (odd/tasks/notification-tasks.md,
 * S8): GET/PUT/DELETE /me/delegation and GET /me/delegation/candidates.
 * While the range is active, the tasks written for the user also reach the
 * delegate — as a task when the delegate already holds the capability, else as
 * an informational notice. No capability is ever lent.
 *
 * "to not in the past" and "an active user of the same company, not yourself"
 * depend on the request (today, the tenant) and are checked by the route.
 */
import { z } from 'zod';

export const MAX_DELEGATION_DAYS = 90;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Calendar date "YYYY-MM-DD" that exists (no 2026-13-01). */
const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inválida.')
  .refine((s) => !Number.isNaN(Date.parse(`${s}T00:00:00Z`)) && new Date(`${s}T00:00:00Z`).toISOString().startsWith(s), 'Fecha inválida.');

/** Days covered by the range, both ends included. */
export function delegationDays(startsOn: string, endsOn: string): number {
  return Math.round((Date.parse(`${endsOn}T00:00:00Z`) - Date.parse(`${startsOn}T00:00:00Z`)) / DAY_MS) + 1;
}

export const PutMyDelegationBodySchema = z
  .object({
    delegateUserId: z.string().uuid(),
    startsOn: IsoDateSchema,
    endsOn: IsoDateSchema,
  })
  .superRefine((body, ctx) => {
    if (body.startsOn > body.endsOn) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endsOn'], message: 'La fecha "Hasta" no puede ser anterior a "Desde".' });
    } else if (delegationDays(body.startsOn, body.endsOn) > MAX_DELEGATION_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endsOn'],
        message: `La delegación puede durar como máximo ${MAX_DELEGATION_DAYS} días.`,
      });
    }
  });
export type PutMyDelegationBody = z.infer<typeof PutMyDelegationBodySchema>;

export const MyDelegationSchema = z.object({
  delegateUserId: z.string().uuid(),
  /** "Nombre Apellido", else the email. */
  delegateName: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
  /** Today falls within the range. */
  active: z.boolean(),
});
export type MyDelegation = z.infer<typeof MyDelegationSchema>;

/** `delegation` is null when there is none, or it already ended. */
export const MyDelegationResponseSchema = z.object({ delegation: MyDelegationSchema.nullable() });
export type MyDelegationResponse = z.infer<typeof MyDelegationResponseSchema>;

/** Who the user can delegate to: the other active users of the company. */
export const DelegationCandidatesResponseSchema = z.object({
  items: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});
export type DelegationCandidatesResponse = z.infer<typeof DelegationCandidatesResponseSchema>;
